import { useMemo, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { shuffle, startCase } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './MarketingPoll.module.css';
import PollOptionButton, { TPollOption } from './PollOptionButton';
import PollOptionDetail from './PollOptionDetail';
import { useLocale } from '../../hooks/useLocale';

export type TMarketingForm = {
  answer: string;
  detail?: string;
};

export const UK_MARKETING_POLL: TPollOption[] = [
  { id: 'ai', name: 'AI Chatbot' },
  { id: 'events', name: 'Events' },
  { id: 'radio', name: 'Radio' },
  { id: 'social_media', name: 'Social media' },
  { id: 'in_the_news', name: 'In the news' },
  { id: 'letter', name: 'Letter' },
  { id: 'train_or_bus_ad', name: 'Bus, Tube or Rail advert' },
  { id: 'search_engine', name: 'Search engine' },
  { id: 'word_of_mouth', name: 'Word of mouth' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'other', name: 'Other' },
];

export const DE_MARKETING_POLL: TPollOption[] = [
  { id: 'search_engine', name: msg`Google/Search engine` },
  { id: 'ai', name: msg`AI Chatbot (e.g. ChatGPT)` },
  { id: 'word_of_mouth', name: msg`Recommendation (colleagues, friends)` },
  { id: 'letter', name: msg`Letter` },
  { id: 'social_media', name: msg`Social media` },
  { id: 'phone_call', name: msg`Direct contact from iwoca` },
  { id: 'other', name: msg({ message: 'Other', context: 'Marketing poll' }) },
];

const MarketingPoll = () => {
  const [submitPoll, setSubmitPoll] = useState(false);

  const { isUK } = useLocale();

  const methods = useForm<TMarketingForm>();

  const { watch, handleSubmit } = methods;

  const pollOptions = useMemo(() => {
    const marketingPoll = isUK ? UK_MARKETING_POLL : DE_MARKETING_POLL;

    const options = marketingPoll.filter((item) => item.id !== 'other');
    const otherOption = marketingPoll.find((item) => item.id === 'other');

    const shuffledOptions = shuffle(options);

    return otherOption ? [...shuffledOptions, otherOption] : shuffledOptions;
  }, [isUK]);

  const onSubmit = (payload: TMarketingForm) => {
    if (!payload.answer) return;

    if (payload.answer) {
      sessionStorage.setItem('marketing_poll_answer', payload.answer || '');
    }

    if (payload.detail) {
      sessionStorage.setItem('marketing_poll_detail', payload.detail);
    }

    if (payload.answer === 'other' && !payload.detail) return;

    tracking.ampli.exitPollSelected({ channelName: startCase(payload.answer) });

    setSubmitPoll(true);
  };

  if (submitPoll) {
    return (
      <section>
        <h3>
          <Trans>Thank you!</Trans>
        </h3>
        {isUK && <p>We value your input.</p>}
      </section>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <section>
          <h3>
            <Trans>Can you tell us where you heard about iwoca?</Trans>
          </h3>
          <div
            data-testid="marketing-poll"
            className={styles.marketingPollContainer}
          >
            {pollOptions.map((poll: TPollOption) => {
              return (
                <PollOptionButton
                  key={poll.id}
                  option={poll}
                  onChange={handleSubmit(onSubmit)}
                />
              );
            })}
          </div>
        </section>
        {watch('answer') === 'other' && <PollOptionDetail />}
      </form>
    </FormProvider>
  );
};

export default MarketingPoll;
